import { useEffect, useMemo, useState } from "react";
import { Copy, Check, ExternalLink } from "lucide-react";
import { api, money } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";

const KEY_STORE = "kc_admin_key";

export default function AdminVideoCodes() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(KEY_STORE) || "");
  const [codes, setCodes] = useState(null);
  const [q, setQ] = useState("");
  const [copied, setCopied] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = async (key = adminKey) => {
    try {
      const r = await api.get("/video-codes", { headers: { "X-Admin-Key": key } });
      setCodes(r.data.codes || []);
      localStorage.setItem(KEY_STORE, key);
    } catch {
      setCodes(null);
      if (key) toast.error("Invalid admin key.");
    }
  };

  useEffect(() => { if (adminKey) load(adminKey); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(`#${code}`);
      setCopied(code);
      setTimeout(() => setCopied(null), 1500);
    } catch { toast.error(`Code is #${code} — copy it manually.`); }
  };

  const resync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/video-codes/backfill", {}, { headers: { "X-Admin-Key": adminKey } });
      toast.success(r.data.assigned ? `${r.data.assigned} new codes assigned.` : "All products already have codes.");
      load();
    } catch { toast.error("Could not assign codes."); }
    finally { setSyncing(false); }
  };

  const filtered = useMemo(() => {
    if (!codes) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return codes;
    return codes.filter((c) => c.title.toLowerCase().includes(needle) || String(c.video_code).includes(needle));
  }, [codes, q]);

  const inputCls = "border border-[#E5E5DF] px-3 py-2.5 text-sm font-body focus:outline-none focus:border-[#1A1A1A]";

  return (
    <main className="max-w-[1000px] mx-auto px-5 sm:px-8 py-14 min-h-[70vh]" data-testid="admin-video-codes-page">
      <Seo title="Video Codes Admin" path="/admin/video-codes" noindex />
      <div className="overline text-[#B84A3B] mb-2">Store Admin</div>
      <h1 className="font-display text-4xl tracking-tight mb-2">Video Codes</h1>
      <p className="text-sm text-[#6E6E69] mb-8 font-body">
        Every product has a permanent number. Type it on your Instagram / TikTok / Facebook video —
        shoppers enter it on the site and land on that exact piece. New products get numbered automatically after each sync.
      </p>

      <div className="flex flex-wrap items-end gap-3 mb-8">
        <label className="max-w-xs flex-1 min-w-[220px]">
          <span className="overline text-[#1A1A1A] block mb-1.5">Admin Key</span>
          <div className="flex gap-2">
            <input type="password" value={adminKey} onChange={(e) => setAdminKey(e.target.value)} placeholder="Enter admin key"
              data-testid="admin-key-input" className={`${inputCls} flex-1`} />
            <button onClick={() => load()} data-testid="admin-load-button"
              className="px-4 border border-[#1A1A1A] text-[0.65rem] font-mono tracking-[0.15em] uppercase hover:bg-[#1A1A1A] hover:text-white transition-colors">Load</button>
          </div>
        </label>
        {codes !== null && (
          <>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or code…"
              data-testid="codes-search" className={`${inputCls} flex-1 min-w-[200px]`} />
            <button onClick={resync} disabled={syncing} data-testid="assign-missing-button"
              className="px-4 py-2.5 border border-[#1A1A1A] text-[0.65rem] font-mono tracking-[0.15em] uppercase hover:bg-[#1A1A1A] hover:text-white transition-colors disabled:opacity-50">
              {syncing ? "…" : "Number New Products"}
            </button>
          </>
        )}
      </div>

      {codes !== null && (
        <p className="text-xs text-[#6E6E69] font-body mb-4" data-testid="codes-count">
          {filtered.length} of {codes.length} products shown
        </p>
      )}

      {codes !== null && (
        <div className="space-y-2" data-testid="codes-list">
          {filtered.slice(0, 300).map((c) => (
            <div key={c.slug} className="flex items-center gap-4 border border-[#E5E5DF] p-2.5" data-testid={`code-row-${c.video_code}`}>
              {c.images?.[0]
                ? <img src={c.images[0]} alt="" className="w-11 h-14 object-cover bg-[#EFEFEA] shrink-0" />
                : <div className="w-11 h-14 bg-[#EFEFEA] shrink-0" />}
              <button onClick={() => copy(c.video_code)} data-testid={`copy-code-${c.video_code}`}
                className="shrink-0 w-20 text-center border border-[#1A1A1A] py-2 font-mono text-sm hover:bg-[#1A1A1A] hover:text-white transition-colors"
                title="Copy this code">
                {copied === c.video_code ? <Check size={14} className="inline" /> : `#${c.video_code}`}
              </button>
              <div className="flex-1 min-w-0">
                <div className="font-body text-sm truncate">{c.title}</div>
                <div className="text-[0.68rem] text-[#6E6E69] font-body">{money(c.price)}{c.in_stock === false && <span className="text-[#B84A3B]"> · out of stock</span>}</div>
              </div>
              <a href={`/product/${c.slug}`} target="_blank" rel="noreferrer" data-testid={`view-${c.video_code}`}
                className="p-2 text-[#6E6E69] hover:text-[#1A1A1A] transition-colors" title="Open product page">
                <ExternalLink size={15} />
              </a>
            </div>
          ))}
          {filtered.length > 300 && (
            <p className="text-xs text-[#6E6E69] font-body pt-2">Showing first 300 — use search to narrow down.</p>
          )}
        </div>
      )}
    </main>
  );
}
